import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import "./AdminConfigManager.css";

const emptyCategory = {
  name: "",
  slug: "",
  sortOrder: 100,
  active: true,
};

const emptyCommunity = {
  value: "",
  label: "",
  slug: "",
  flag: "",
  aliases: "",
  sortOrder: 100,
  active: true,
  showOnHub: false,
  comingSoon: false,
};

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function AdminConfigManager({
  apiBase,
  adminKey,
}) {
  const [mode, setMode] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] = useState("");
  const [message, setMessage] =
    useState("");

  const [editingSlug, setEditingSlug] =
    useState("");

  const [categoryForm, setCategoryForm] =
    useState(emptyCategory);

  const [communityForm, setCommunityForm] =
    useState(emptyCommunity);

  const base = String(
    apiBase || ""
  ).replace(/\/+$/, "");

  const endpoint = useMemo(() => {
    if (!mode) {
      return "";
    }

    return `${base}/api/config/admin/${mode}`;
  }, [base, mode]);

  const cleanKey = String(
    adminKey || ""
  ).trim();

  const loadItems = async () => {
    if (!mode) {
      return;
    }

    if (!cleanKey) {
      setItems([]);
      setError(
        "Save your Admin API key above first."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        endpoint,
        {
          method: "GET",
          headers: {
            "X-Admin-API-Key":
              cleanKey,
          },
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(
          data.message ||
            `Request failed (${response.status})`
        );
      }

      setItems(
        Array.isArray(data[mode])
          ? data[mode]
          : []
      );
    } catch (err) {
      setError(
        err?.message ||
          "Unable to load configuration."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode && cleanKey) {
      loadItems();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const resetForm = () => {
    setEditingSlug("");

    if (mode === "categories") {
      setCategoryForm(
        emptyCategory
      );
    }

    if (mode === "communities") {
      setCommunityForm(
        emptyCommunity
      );
    }
  };

  const switchMode = (nextMode) => {
    if (mode === nextMode) {
      setMode("");
      setItems([]);
      setError("");
      setMessage("");
      resetForm();
      return;
    }

    setMode(nextMode);
    setItems([]);
    setError("");
    setMessage("");
    setEditingSlug("");

    if (nextMode === "categories") {
      setCategoryForm(
        emptyCategory
      );
    } else {
      setCommunityForm(
        emptyCommunity
      );
    }
  };

  const editItem = (item) => {
    setError("");
    setMessage("");

    setEditingSlug(
      item.slug || ""
    );

    if (mode === "categories") {
      setCategoryForm({
        name: item.name || "",
        slug: item.slug || "",
        sortOrder:
          Number(item.sortOrder) ||
          100,
        active:
          item.active !== false,
      });

      return;
    }

    setCommunityForm({
      value: item.value || "",
      label:
        item.label ||
        item.value ||
        "",
      slug: item.slug || "",
      flag: item.flag || "",
      aliases: Array.isArray(
        item.aliases
      )
        ? item.aliases.join(", ")
        : "",
      sortOrder:
        Number(item.sortOrder) ||
        100,
      active:
        item.active !== false,
      showOnHub:
        item.showOnHub === true,
      comingSoon:
        item.comingSoon === true,
    });
  };

  const saveItem = async (
    event
  ) => {
    event.preventDefault();

    if (!cleanKey) {
      setError(
        "Admin API key is required."
      );
      return;
    }

    let body;

    if (mode === "categories") {
      const name =
        categoryForm.name.trim();

      if (!name) {
        setError(
          "Category name is required."
        );
        return;
      }

      body = {
        name,
        slug:
          categoryForm.slug.trim() ||
          slugify(name),
        sortOrder:
          Number(
            categoryForm.sortOrder
          ) || 100,
        active:
          categoryForm.active,
      };
    } else {
      const value =
        communityForm.value.trim();

      if (!value) {
        setError(
          "Community name is required."
        );
        return;
      }

      body = {
        value,
        label:
          communityForm.label.trim() ||
          value,
        slug:
          communityForm.slug.trim() ||
          slugify(value),
        flag:
          communityForm.flag
            .trim()
            .toLowerCase() ||
          "un",

        aliases: communityForm.aliases
          .split(",")
          .map((item) =>
            item.trim()
          )
          .filter(Boolean),

        sortOrder:
          Number(
            communityForm.sortOrder
          ) || 100,

        active:
          communityForm.active,

        showOnHub:
          communityForm.showOnHub,

        comingSoon:
          communityForm.comingSoon,
      };
    }

    const isEditing =
      Boolean(editingSlug);

    const url = isEditing
      ? `${endpoint}/${encodeURIComponent(
          editingSlug
        )}`
      : endpoint;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(
        url,
        {
          method: isEditing
            ? "PUT"
            : "POST",

          headers: {
            "Content-Type":
              "application/json",

            "X-Admin-API-Key":
              cleanKey,
          },

          body:
            JSON.stringify(body),
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(
          data.message ||
            `Save failed (${response.status})`
        );
      }

      setMessage(
        isEditing
          ? `${
              mode === "categories"
                ? "Category"
                : "Community"
            } updated.`
          : `${
              mode === "categories"
                ? "Category"
                : "Community"
            } added.`
      );

      resetForm();

      await loadItems();
    } catch (err) {
      setError(
        err?.message ||
          "Unable to save."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (
    item
  ) => {
    if (!cleanKey) {
      setError(
        "Admin API key is required."
      );
      return;
    }

    const nextActive =
      item.active === false;

    setError("");
    setMessage("");

    try {
      const response = await fetch(
        `${endpoint}/${encodeURIComponent(
          item.slug
        )}`,
        {
          method: "PUT",

          headers: {
            "Content-Type":
              "application/json",

            "X-Admin-API-Key":
              cleanKey,
          },

          body: JSON.stringify({
            active: nextActive,
          }),
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(
          data.message ||
            "Unable to update status."
        );
      }

      setMessage(
        `${
          mode === "categories"
            ? "Category"
            : "Community"
        } ${
          nextActive
            ? "activated"
            : "deactivated"
        }.`
      );

      await loadItems();
    } catch (err) {
      setError(
        err?.message ||
          "Unable to update status."
      );
    }
  };

  const title =
    mode === "categories"
      ? "Business Categories"
      : "Communities";

  return (
    <section className="admin-config">
      <div className="admin-config-heading">
        <div>
          <span className="admin-config-kicker">
            Configuration
          </span>

          <h2>
            Manage GoOurHub
          </h2>

          <p>
            Add and maintain categories
            and communities without
            touching the server console.
          </p>
        </div>

        <div className="admin-config-switcher">
          <button
            type="button"
            className={
              mode === "categories"
                ? "is-active"
                : ""
            }
            onClick={() =>
              switchMode(
                "categories"
              )
            }
          >
            Categories
          </button>

          <button
            type="button"
            className={
              mode === "communities"
                ? "is-active"
                : ""
            }
            onClick={() =>
              switchMode(
                "communities"
              )
            }
          >
            Communities
          </button>
        </div>
      </div>

      {mode && (
        <div className="admin-config-panel">
          <div className="admin-config-panel-head">
            <div>
              <h3>{title}</h3>

              <span>
                {items.length} records
              </span>
            </div>

            <button
              type="button"
              className="admin-config-refresh"
              onClick={loadItems}
              disabled={loading}
            >
              {loading
                ? "Loading…"
                : "Refresh"}
            </button>
          </div>

          {!cleanKey && (
            <div className="admin-config-warning">
              Save your Admin API key
              above before managing
              configuration.
            </div>
          )}

          {message && (
            <div className="admin-config-message success">
              {message}
            </div>
          )}

          {error && (
            <div className="admin-config-message error">
              {error}
            </div>
          )}

          <form
            className="admin-config-form"
            onSubmit={saveItem}
          >
            <h4>
              {editingSlug
                ? `Edit ${
                    mode ===
                    "categories"
                      ? "category"
                      : "community"
                  }`
                : `Add ${
                    mode ===
                    "categories"
                      ? "category"
                      : "community"
                  }`}
            </h4>

            {mode ===
            "categories" ? (
              <div className="admin-config-form-grid">
                <label>
                  <span>Name</span>

                  <input
                    value={
                      categoryForm.name
                    }
                    onChange={(event) =>
                      setCategoryForm(
                        (current) => ({
                          ...current,
                          name:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                    placeholder="Accounting & Tax Services"
                  />
                </label>

                <label>
                  <span>Slug</span>

                  <input
                    value={
                      categoryForm.slug
                    }
                    onChange={(event) =>
                      setCategoryForm(
                        (current) => ({
                          ...current,
                          slug:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                    placeholder="Auto-generated if blank"
                  />
                </label>

                <label>
                  <span>
                    Sort order
                  </span>

                  <input
                    type="number"
                    value={
                      categoryForm.sortOrder
                    }
                    onChange={(event) =>
                      setCategoryForm(
                        (current) => ({
                          ...current,
                          sortOrder:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                  />
                </label>

                <label className="admin-config-checkbox">
                  <input
                    type="checkbox"
                    checked={
                      categoryForm.active
                    }
                    onChange={(event) =>
                      setCategoryForm(
                        (current) => ({
                          ...current,
                          active:
                            event
                              .target
                              .checked,
                        })
                      )
                    }
                  />

                  <span>Active</span>
                </label>
              </div>
            ) : (
              <div className="admin-config-form-grid">
                <label>
                  <span>
                    Community name
                  </span>

                  <input
                    value={
                      communityForm.value
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          value:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                    placeholder="Pakistani"
                  />
                </label>

                <label>
                  <span>
                    Display label
                  </span>

                  <input
                    value={
                      communityForm.label
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          label:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                    placeholder="Pakistani"
                  />
                </label>

                <label>
                  <span>Slug</span>

                  <input
                    value={
                      communityForm.slug
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          slug:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                    placeholder="Auto-generated"
                  />
                </label>

                <label>
                  <span>
                    Flag code
                  </span>

                  <input
                    value={
                      communityForm.flag
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          flag:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                    maxLength={2}
                    placeholder="pk"
                  />
                </label>

                <label>
                  <span>Aliases</span>

                  <input
                    value={
                      communityForm.aliases
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          aliases:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                    placeholder="Comma separated"
                  />
                </label>

                <label>
                  <span>
                    Sort order
                  </span>

                  <input
                    type="number"
                    value={
                      communityForm.sortOrder
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          sortOrder:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                  />
                </label>

                <label className="admin-config-checkbox">
                  <input
                    type="checkbox"
                    checked={
                      communityForm.active
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          active:
                            event
                              .target
                              .checked,
                        })
                      )
                    }
                  />

                  <span>Active</span>
                </label>

                <label className="admin-config-checkbox">
                  <input
                    type="checkbox"
                    checked={
                      communityForm.showOnHub
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          showOnHub:
                            event
                              .target
                              .checked,
                        })
                      )
                    }
                  />

                  <span>
                    Show on Hub
                  </span>
                </label>

                <label className="admin-config-checkbox">
                  <input
                    type="checkbox"
                    checked={
                      communityForm.comingSoon
                    }
                    onChange={(event) =>
                      setCommunityForm(
                        (current) => ({
                          ...current,
                          comingSoon:
                            event
                              .target
                              .checked,
                        })
                      )
                    }
                  />

                  <span>
                    Coming soon
                  </span>
                </label>
              </div>
            )}

            <div className="admin-config-form-actions">
              <button
                type="submit"
                disabled={
                  saving ||
                  !cleanKey
                }
              >
                {saving
                  ? "Saving…"
                  : editingSlug
                  ? "Save changes"
                  : "Add"}
              </button>

              {editingSlug && (
                <button
                  type="button"
                  className="ghost"
                  onClick={resetForm}
                >
                  Cancel edit
                </button>
              )}
            </div>
          </form>

          <div className="admin-config-list">
            {loading ? (
              <div className="admin-config-empty">
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="admin-config-empty">
                No {mode} found.
              </div>
            ) : (
              items.map((item) => (
                <article
                  className="admin-config-row"
                  key={item.slug}
                >
                  <div className="admin-config-main">
                    <strong>
                      {mode ===
                      "categories"
                        ? item.name
                        : item.label ||
                          item.value}
                    </strong>

                    <span>
                      {item.slug}
                    </span>

                    {mode ===
                      "communities" &&
                      item.flag && (
                        <small>
                          Flag:{" "}
                          {String(
                            item.flag
                          ).toUpperCase()}
                        </small>
                      )}
                  </div>

                  <div className="admin-config-meta">
                    <span
                      className={
                        item.active ===
                        false
                          ? "admin-config-status inactive"
                          : "admin-config-status active"
                      }
                    >
                      {item.active ===
                      false
                        ? "Inactive"
                        : "Active"}
                    </span>

                    <small>
                      Order{" "}
                      {item.sortOrder ??
                        "—"}
                    </small>
                  </div>

                  <div className="admin-config-actions">
                    <button
                      type="button"
                      onClick={() =>
                        editItem(item)
                      }
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className={
                        item.active ===
                        false
                          ? "activate"
                          : "deactivate"
                      }
                      onClick={() =>
                        toggleActive(
                          item
                        )
                      }
                    >
                      {item.active ===
                      false
                        ? "Activate"
                        : "Deactivate"}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      )}
    </section>
  );
}